import { isBlackKey } from "./keyboard";

const blackKeyHeightRatio = 0.5;
const blackKeyWidthRatio = 0.85;

export type Coordinates = {
  x: number;
  y: number;
};

export type Dimensions = {
  height: number;
  width: number;
}

export interface CartesianLayoutManager<T> {
  getBounds: (item: T) => Coordinates & Dimensions;
}

type Key = number;

type KeyBoundsMap = {
  [key: number]: Coordinates & Dimensions;
}

interface Options {
  blackKeyWidthRatio: number;
  blackKeyHeightRatio: number;
}

interface LayoutManagerProps {
  canvasDimensions: Dimensions;
  keys: Array<number>;
  options?: Partial<Options>;
}

class KeyboardLayoutManager implements CartesianLayoutManager<Key> {
  // Pre-calculate the bounds for the managed keyboard keys
  static getKeyBoundsMap(keys: Array<number>, canvasDimensions: Dimensions, options: Options) {
    const whiteKeyWidth = canvasDimensions.width / (keys.filter(k => !isBlackKey(k)).length || 1);
    const whiteKeyHeight = canvasDimensions.height;
    const blackKeyWidth = whiteKeyWidth * options.blackKeyWidthRatio;
    const blackKeyHeight = whiteKeyHeight * options.blackKeyHeightRatio;

    let accX = 0;

    return keys.reduce((memo, k, i) => {
      const isBlkKey = isBlackKey(k);

      accX += isBlkKey ? 0 : (i ? whiteKeyWidth : 0);

      return {
        ...memo,
        [k]: {
          x: isBlkKey ? accX + (whiteKeyWidth - (blackKeyWidth / 2)) : accX,
          y: 0,
          width: isBlkKey ? blackKeyWidth : whiteKeyWidth,
          height: isBlkKey ? blackKeyHeight : whiteKeyHeight
        }
      }
    }, {});
  }

  private canvasDimensions: Dimensions;
  private keys: Array<number>;
  private options: Options;
  private keyBoundsMap: KeyBoundsMap;

  constructor(props: LayoutManagerProps) {
    this.keys = props.keys;
    this.canvasDimensions = props.canvasDimensions;
    this.options = {
      blackKeyHeightRatio,
      blackKeyWidthRatio,
      ...(props.options || {})
    }
    this.keyBoundsMap = KeyboardLayoutManager.getKeyBoundsMap(this.keys, this.canvasDimensions, this.options);
  }

  getBounds(key: Key) {
    const bounds = this.keyBoundsMap[key];

    if (!bounds) {
      throw new Error(`Key not in keyboard range: ${JSON.stringify(key)}`)
    }

    return bounds;
  }
}

export default KeyboardLayoutManager;
